/*
 * component-platmart-swatches.js
 *
 * Reads window.PLATMART_SWATCHES (loaded from platmart-swatch-data.js) and
 * populates the color swatch UI on product cards (collection pages, home,
 * cart recommendations) and on the product page color picker.
 *
 * Layout (matches EU site):
 *
 *  --- Product cards (collection page, home, etc) ---
 *    Desktop (>=1024px):
 *      Default: show the current color name as grey text below the title.
 *      On hover: hide the text and show a horizontal row of circular swatches
 *                for every sibling color in the same Platmart group.
 *    Mobile (<1024px):
 *      Always show the swatches (no hover, no color name text).
 *
 *  --- Product page color picker ---
 *      Show "Color : <current color name>" label, then a horizontal row of
 *      small product photos (one per sibling). The current color is bordered.
 *
 * The script never blocks page rendering: card swatches are populated after
 * DOMContentLoaded and after each cart-recommendations / quickbuy render.
 */
(function () {
  if (typeof window === 'undefined') return;

  function getData() {
    return window.PLATMART_SWATCHES || null;
  }

  // Find the group + sibling list for a given product handle.
  function lookup(handle) {
    var data = getData();
    if (!data || !data.h || !data.g) return null;
    var key = data.h[handle];
    if (!key) return null;
    var group = data.g[key];
    if (!group) return null;
    return {
      title: group.t,
      members: group.m || []
    };
  }

  // Build a single circular swatch DOM node for a card.
  function buildCardSwatch(member, isCurrent, productUrl) {
    var li = document.createElement('li');
    li.className = 'product-card-color-swatch' + (isCurrent ? ' product-card-color-swatch--current' : '');
    li.setAttribute('data-handle', member.h);
    li.setAttribute('title', member.n || '');

    var a = document.createElement('a');
    a.href = '/products/' + member.h;
    a.setAttribute('aria-label', member.n || member.h);

    var swatch = document.createElement('span');
    swatch.className = 'product-card-color-swatch__dot';

    if (member.t === 'custom_image' && member.img) {
      swatch.style.backgroundImage = 'url("' + member.img + '")';
      swatch.style.backgroundSize = 'cover';
      swatch.style.backgroundPosition = 'center';
    } else if (member.t === 'one_color' && member.c1) {
      swatch.style.backgroundColor = member.c1;
      if (member.c2) {
        swatch.style.background = 'linear-gradient(-45deg,' + member.c1 + ' 0%,' + member.c1 + ' 50%,' + member.c2 + ' 50%,' + member.c2 + ' 100%)';
      }
      if (member.c1.toLowerCase() === '#ffffff' || member.c1.toLowerCase() === '#fff') {
        swatch.style.borderColor = '#EFEFEF';
      }
    } else if (member.t === 'product_image' && member.img) {
      swatch.style.backgroundImage = 'url("' + member.img + '")';
      swatch.style.backgroundSize = 'cover';
      swatch.style.backgroundPosition = 'center';
    } else {
      // Fallback: light grey
      swatch.style.backgroundColor = '#EAEAEA';
    }

    a.appendChild(swatch);
    li.appendChild(a);
    return li;
  }

  // Populate one product card's swatch row and color-name text.
  function populateCard(card) {
    if (!card || card.getAttribute('data-platmart-done') === '1') return;
    var handle = card.getAttribute('data-handle');
    if (!handle) return;

    var info = lookup(handle);
    if (!info) {
      // Not in Platmart group: nothing to do, mark done so we don't retry.
      card.setAttribute('data-platmart-done', '1');
      return;
    }

    // Find the current member (current color name to display as default text).
    var current = null;
    for (var i = 0; i < info.members.length; i++) {
      if (info.members[i].h === handle) { current = info.members[i]; break; }
    }
    var currentName = current ? current.n : '';

    // Container for swatches: ".product-card-colors" (existing slot).
    var ul = card.querySelector('.product-card-colors');
    if (ul) {
      ul.innerHTML = ''; // wipe any metafield-based render
      var max = 5;
      var shown = 0;
      for (var j = 0; j < info.members.length && shown < max; j++) {
        var isCurrent = info.members[j].h === handle;
        ul.appendChild(buildCardSwatch(info.members[j], isCurrent, info.members[j].h));
        shown++;
      }
      if (info.members.length > max) {
        var more = document.createElement('span');
        more.className = 'product-card-colors__size u-nowrap u-philippine-gray';
        more.textContent = '+' + (info.members.length - max);
        ul.appendChild(more);
      }
    }

    // Set the default (non-hover) color-name text on desktop. The existing
    // ".product-card-type" span sits below the title. Replace its text.
    var typeEl = card.querySelector('.product-card-type');
    if (typeEl && currentName) {
      typeEl.textContent = currentName;
    } else if (!typeEl && currentName) {
      // Title doesn't contain " - ", so the theme didn't render a type span.
      // Insert one so the desktop default shows the color name.
      var wrap = card.querySelector('.product-card__colors-wrapper');
      if (wrap) {
        var span = document.createElement('span');
        span.className = 'product-card-type u-block u-left u-philippine-gray';
        span.textContent = currentName;
        wrap.insertBefore(span, wrap.firstChild);
      }
    }

    card.setAttribute('data-platmart-done', '1');
  }

  function populateAllCards(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll('.product-card[data-handle]');
    cards.forEach(populateCard);
  }

  // ---------- Product page color picker ----------
  // Cache of /products/<handle>.js responses so we don't re-fetch when
  // the user navigates between color variants in the same group.
  var productCache = {};

  function fetchProductData(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) productCache[handle] = data;
        return data;
      })
      .catch(function () { return null; });
  }

  function populateProductPagePicker() {
    var container = document.querySelector('[data-platmart-product-colors]');
    if (!container || container.getAttribute('data-platmart-done') === '1') return;
    var handle = container.getAttribute('data-product-handle');
    if (!handle) return;

    var info = lookup(handle);
    if (!info) {
      container.setAttribute('data-platmart-done', '1');
      container.style.display = 'none';
      return;
    }

    // Reveal the container now that we know this product is in a Platmart
    // group (the snippet starts with style="display:none" to avoid a
    // flash of empty markup).
    container.style.display = '';

    var current = null;
    for (var i = 0; i < info.members.length; i++) {
      if (info.members[i].h === handle) { current = info.members[i]; break; }
    }

    // Label "Color : <current name>"
    var label = container.querySelector('[data-platmart-color-label]');
    if (label && current) {
      var labelText = label.getAttribute('data-label-prefix') || 'Color';
      label.textContent = labelText + ' : ' + current.n;
    }

    // Render one li per sibling. Image starts as a tinted placeholder
    // (using the Platmart swatch as a fallback), then we fetch the
    // sibling's real featured_image and swap it in.
    var list = container.querySelector('[data-platmart-color-list]');
    if (!list) {
      container.setAttribute('data-platmart-done', '1');
      return;
    }
    list.innerHTML = '';

    info.members.forEach(function (m) {
      var li = document.createElement('li');
      li.className = 'product-color-image' + (m.h === handle ? ' product-color-image--current' : '');
      li.setAttribute('data-handle', m.h);
      li.setAttribute('title', m.n || '');

      var a = document.createElement('a');
      a.href = '/products/' + m.h;
      a.setAttribute('aria-label', m.n || m.h);

      var imgWrap = document.createElement('span');
      imgWrap.className = 'product-color-image__inner';

      // Placeholder: use Platmart swatch (small color chip) or hex fill
      // while we fetch the real featured image.
      if (m.t === 'one_color' && m.c1) {
        imgWrap.style.backgroundColor = m.c1;
      } else {
        imgWrap.style.backgroundColor = '#EAEAEA';
      }

      var img = document.createElement('img');
      img.alt = m.n || '';
      img.loading = 'lazy';
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.2s';
      imgWrap.appendChild(img);

      a.appendChild(imgWrap);
      li.appendChild(a);
      list.appendChild(li);

      // Fetch the sibling's product JSON to get the real featured_image.
      fetchProductData(m.h).then(function (data) {
        if (!data) return;
        var src = null;
        if (data.featured_image) {
          src = data.featured_image;
        } else if (data.images && data.images[0]) {
          src = data.images[0];
        }
        if (!src) return;
        // Shopify image URLs can take a size suffix. Use a small one.
        var sized = src.replace(/(\.(?:jpg|jpeg|png|webp))/i, '_240x320$1');
        img.src = sized;
        img.onload = function () { img.style.opacity = '1'; };
        // Fallback if the resized version fails: use the original.
        img.onerror = function () {
          img.onerror = null;
          img.src = src;
        };
      });
    });

    container.setAttribute('data-platmart-done', '1');
  }

  // ---------- Boot + re-runs ----------
  function init() {
    populateAllCards();
    populateProductPagePicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when content is added dynamically (cart recommendations, quickbuy,
  // filtered collection refreshes, infinite-scroll). Watch the body for new
  // .product-card nodes.
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue; // element only
          if (node.matches && node.matches('.product-card[data-handle]')) {
            populateCard(node);
          } else if (node.querySelectorAll) {
            var inner = node.querySelectorAll('.product-card[data-handle]');
            if (inner.length) inner.forEach(populateCard);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Expose for manual triggering (e.g., after AJAX collection updates).
  window.platmartRefreshSwatches = function (root) { populateAllCards(root); };
})();
